import React from 'react';
import { Box, TextField } from '@mui/material';
import { FormSection } from '../../form/FormSection';
import { SearchableDropdown } from '../../form/SearchableDropdown';
import { FORM_SECTIONS } from '../../../config/formSections';
import { FORM_STYLES } from '../../../styles/formStyles';
import { FormValues } from '../CreateStep1V3';
import { Housing } from '../../../api/types';

interface HousingSectionProps {
  values: FormValues;
  handleChange: (name: string) => (value: string | number | boolean) => void;
  setOtherReason: (val: string | null) => void;
  otherReason: string | null;
  associationsList: string[];
  availableApartments: Housing[];
  reasons: Array<{ value: string; title: string }>;
  onAssociationChange: (association: string) => void;
}

export const HousingSection: React.FC<HousingSectionProps> = ({
  values,
  handleChange,
  setOtherReason,
  otherReason,
  associationsList,
  availableApartments,
  reasons,
  onAssociationChange
}) => {
  const sectionConfig = FORM_SECTIONS.housing;

  const handleApartmentChange = (value: string) => {
    const selectedHousing = availableApartments.find((housing) =>
      value === `${housing.apartmentNumber} - ${housing.address} (${housing.housingCooperative})`
    );
    
    if (selectedHousing) {
      handleChange('selectedApartment')(value);
      handleChange('selectedAssociation')(selectedHousing.housingCooperative || '');
      handleChange('address')(selectedHousing.address);
      handleChange('city')(selectedHousing.city);
      handleChange('zipCode')(selectedHousing.zipCode);
    }
  };

  // Enhanced apartment options that include housing cooperative name
  const apartmentOptions = availableApartments.map(housing => ({
    value: `${housing.apartmentNumber} - ${housing.address} (${housing.housingCooperative})`,
    label: `Lgh ${housing.apartmentNumber} - ${housing.address} (${housing.housingCooperative || 'Okänd förening'})`
  }));

  const reasonOptions = reasons.map(reason => ({
    value: reason.value,
    label: reason.value
  }));

  return (
    <FormSection
      icon={sectionConfig.icon}
      iconColor={sectionConfig.color}
      title={sectionConfig.titleKey}
    >
      {/* Single column layout - only apartment selection */}
      <Box sx={{ mb: 3 }}>
        <SearchableDropdown
          label="Välj lägenhet"
          value={values.selectedApartment || ''}
          onChange={handleApartmentChange}
          options={apartmentOptions}
          placeholder="Välj din lägenhet..."
          searchPlaceholder="Sök lägenhetsnummer, adress eller förening..."
        />
      </Box>

      {/* Anledning till uthyrning */}
      <Box>
        <SearchableDropdown
          label="Anledning till uthyrning"
          value={values.reason || ''}
          onChange={handleChange('reason')}
          options={reasonOptions}
          placeholder="Välj anledning..."
        />

        {values.reason === reasons.find(r => r.title === 'other_reason')?.value && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={otherReason ?? ''}
              onChange={(val) => setOtherReason(val.target.value ?? null)}
              placeholder="Beskriv din anledning..."
              variant="outlined"
              sx={FORM_STYLES.input.base}
            />
          </Box>
        )}
      </Box>
    </FormSection>
  );
}; 